import React from "react";
import { WithStyles } from "@material-ui/styles";
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import { Bar, Doughnut, HorizontalBar } from "react-chartjs-2";

import styles, { MONTHLY_REPORT_PALETTE } from "./styles";
import { MonthlyReport } from "../../redux/types/reports";
import NterNowLogo from "../../assets/menu-new.svg";

type Props = {
  monthlyReport: MonthlyReport;
  startDate: string | null;
  endDate: string | null;
  isPrint?: boolean;
} & Pick<WithStyles<typeof styles>, "classes">;

const HEATMAP_DAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const HEATMAP_TIMES = [
  "12–2am",
  "2–4am",
  "4–6am",
  "6–8am",
  "8–10am",
  "10am–12pm",
  "12–2pm",
  "2–4pm",
  "4–6pm",
  "6–8pm",
  "8–10pm",
  "10pm–12am"
];

const formatPeriod = (
  startDate: string | null,
  endDate: string | null
): string => {
  if (!startDate || !endDate) {
    return "";
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  const formattedStart = start.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });

  const formattedEnd = end.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });

  return `${formattedStart} - ${formattedEnd}`;
};

const getMissedCancelledTotal = (report: MonthlyReport): number =>
  report.summary.missed + report.summary.cancelled;

const formatPercentage = (value: number, total: number, precision?: number): string => {
  if (!total || total <= 0) {
    return "0.00%";
  }

  return `${((value / total) * 100).toFixed(precision ?? 2)}%`;
};

const formatKpiPercentage = (value: number, precision?: number): string => `${value.toFixed(precision ?? 2)}%`;

const getHeatmapClassName = (
  value: number,
  maxValue: number,
  classes: Props["classes"]
): string => {
  if (value <= 0 || maxValue <= 0) {
    return classes.heatmap0;
  }

  const ratio = value / maxValue;

  if (ratio <= 0.25) return classes.heatmap1;
  if (ratio <= 0.5) return classes.heatmap2;
  if (ratio <= 0.75) return classes.heatmap3;
  return classes.heatmap4;
};

const buildBaseChartOptions = (disableAnimation?: boolean) => ({
  animation: disableAnimation ? false : undefined,
  tooltips: {
    enabled: !disableAnimation
  },
  hover: {
    animationDuration: disableAnimation ? 0 : undefined
  },
  responsiveAnimationDuration: disableAnimation ? 0 : undefined
});

const buildDoughnutOptions = (disableAnimation?: boolean) => ({
  ...buildBaseChartOptions(disableAnimation),
  responsive: true,
  maintainAspectRatio: false,
  cutoutPercentage: 58,
  legend: {
    position: "bottom" as const,
    labels: {
      boxWidth: 10,
      fontSize: 11,
      usePointStyle: true,
      padding: 10
    }
  }
});

const buildBarOptions = (disableAnimation?: boolean) => ({
  ...buildBaseChartOptions(disableAnimation),
  responsive: true,
  maintainAspectRatio: false,
  legend: {
    display: false
  },
  scales: {
    yAxes: [
      {
        ticks: {
          beginAtZero: true,
          precision: 0
        },
        gridLines: {
          color: MONTHLY_REPORT_PALETTE.border,
          drawBorder: false
        }
      }
    ],
    xAxes: [
      {
        ticks: {
          autoSkip: false,
          fontSize: 10
        },
        gridLines: {
          display: false,
          drawBorder: false
        }
      }
    ]
  }
});

const buildHorizontalBarOptions = (
  showLegend?: boolean,
  disableAnimation?: boolean,
  showYAxisPercentages?: boolean
) => {
  const yAxisTicks: any = {
    autoSkip: false,
    fontSize: 10
  };

  if (showYAxisPercentages) {
    yAxisTicks.callback = function(this: any, value: string, index: number) {
      const dataset = this.chart.data.datasets[0] || {};
      const values = dataset.data || [];
      const total = values.reduce(
        (sum: number, item: number) => sum + Number(item || 0),
        0
      );

      return [
        value,
        formatPercentage(Number(values[index] || 0), total)
      ];
    };
  }

  return {
    ...buildBaseChartOptions(disableAnimation),
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: Boolean(showLegend),
      position: "bottom" as const,
      labels: {
        boxWidth: 10,
        fontSize: 11,
        usePointStyle: true,
        padding: 10
      }
    },
    scales: {
      xAxes: [
        {
          ticks: {
            beginAtZero: true,
            precision: 0
          },
          gridLines: {
            color: MONTHLY_REPORT_PALETTE.border,
            drawBorder: false
          }
        }
      ],
      yAxes: [
        {
          ticks: yAxisTicks,
          gridLines: {
            display: false,
            drawBorder: false
          }
        }
      ]
    }
  };
};

const ReportMonthlyContent: React.FC<Props> = props => {
  const { classes, monthlyReport, startDate, endDate, isPrint } = props;

  const sourceTotal =
    monthlyReport.bySource.onSiteAndSchedulingSite +
    monthlyReport.bySource.liveSupport;
  const visitorTypeTotal =
    monthlyReport.byVisitorType.visitor +
    monthlyReport.byVisitorType.agent +
    monthlyReport.byVisitorType.client +
    monthlyReport.byVisitorType.contractor;
  const homeTypeTotal =
    monthlyReport.byHomeType.inventory + monthlyReport.byHomeType.model;

  const sourceChartData = {
    labels: [
      `On Site and Scheduling Site - ${formatPercentage(
        monthlyReport.bySource.onSiteAndSchedulingSite,
        sourceTotal
      )}`,
      `Live Support - ${formatPercentage(
        monthlyReport.bySource.liveSupport,
        sourceTotal
      )}`
    ],
    datasets: [
      {
        data: [
          monthlyReport.bySource.onSiteAndSchedulingSite,
          monthlyReport.bySource.liveSupport
        ],
        backgroundColor: [
          MONTHLY_REPORT_PALETTE.primary500,
          MONTHLY_REPORT_PALETTE.dark500
        ],
        borderWidth: 0
      }
    ]
  };

  const visitorChartData = {
    labels: [
      `Visitor - ${formatPercentage(
        monthlyReport.byVisitorType.visitor,
        visitorTypeTotal
      )}`,
      `Agent - ${formatPercentage(
        monthlyReport.byVisitorType.agent,
        visitorTypeTotal
      )}`,
      `Client - ${formatPercentage(
        monthlyReport.byVisitorType.client,
        visitorTypeTotal
      )}`,
      `Contractor - ${formatPercentage(
        monthlyReport.byVisitorType.contractor,
        visitorTypeTotal
      )}`
    ],
    datasets: [
      {
        data: [
          monthlyReport.byVisitorType.visitor,
          monthlyReport.byVisitorType.agent,
          monthlyReport.byVisitorType.client,
          monthlyReport.byVisitorType.contractor
        ],
        backgroundColor: [
          MONTHLY_REPORT_PALETTE.purple500,
          MONTHLY_REPORT_PALETTE.primary500,
          MONTHLY_REPORT_PALETTE.teal500,
          MONTHLY_REPORT_PALETTE.accent500
        ],
        borderWidth: 0
      }
    ]
  };

  const homeTypeChartData = {
    labels: [
      `Inventory - ${formatPercentage(
        monthlyReport.byHomeType.inventory,
        homeTypeTotal
      )}`,
      `Model - ${formatPercentage(
        monthlyReport.byHomeType.model,
        homeTypeTotal
      )}`
    ],
    datasets: [
      {
        data: [
          monthlyReport.byHomeType.inventory,
          monthlyReport.byHomeType.model
        ],
        backgroundColor: [
          MONTHLY_REPORT_PALETTE.accent500,
          MONTHLY_REPORT_PALETTE.info400
        ],
        borderWidth: 0
      }
    ]
  };

  const statusOverviewData = {
    labels: ["Completed", "Missed", "Cancelled"],
    datasets: [
      {
        data: [
          monthlyReport.summary.completed,
          monthlyReport.summary.missed,
          monthlyReport.summary.cancelled
        ],
        backgroundColor: [
          MONTHLY_REPORT_PALETTE.teal500,
          MONTHLY_REPORT_PALETTE.primary500,
          MONTHLY_REPORT_PALETTE.purple500
        ],
        borderWidth: 0
      }
    ]
  };

  const toursPerVisitorData = {
    labels: ["1 Tour", "2 Tours", "3 Tours", "4 Tours", "5+"],
    datasets: [
      {
        data: [
          monthlyReport.toursPerVisitor.oneTour,
          monthlyReport.toursPerVisitor.twoTours,
          monthlyReport.toursPerVisitor.threeTours,
          monthlyReport.toursPerVisitor.fourTours,
          monthlyReport.toursPerVisitor.fivePlusTours
        ],
        backgroundColor: [
          MONTHLY_REPORT_PALETTE.dark500,
          MONTHLY_REPORT_PALETTE.dark300,
          MONTHLY_REPORT_PALETTE.primary500,
          MONTHLY_REPORT_PALETTE.info400,
          MONTHLY_REPORT_PALETTE.gray300
        ],
        borderWidth: 0
      }
    ]
  };

  const toursByTimeData = {
    labels: ["5am–11am", "11am–2pm", "2pm–6pm", "6pm–5am"],
    datasets: [
      {
        data: [
          monthlyReport.toursByTime.morning,
          monthlyReport.toursByTime.midday,
          monthlyReport.toursByTime.afternoon,
          monthlyReport.toursByTime.evening
        ],
        backgroundColor: [
          MONTHLY_REPORT_PALETTE.info400,
          MONTHLY_REPORT_PALETTE.accent500,
          MONTHLY_REPORT_PALETTE.primary500,
          MONTHLY_REPORT_PALETTE.dark500
        ],
        borderWidth: 0
      }
    ]
  };

  const toursByDayData = {
    labels: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    datasets: [
      {
        data: [
          monthlyReport.toursByDay.sunday,
          monthlyReport.toursByDay.monday,
          monthlyReport.toursByDay.tuesday,
          monthlyReport.toursByDay.wednesday,
          monthlyReport.toursByDay.thursday,
          monthlyReport.toursByDay.friday,
          monthlyReport.toursByDay.saturday
        ],
        backgroundColor: [
          MONTHLY_REPORT_PALETTE.primary500,
          MONTHLY_REPORT_PALETTE.info400,
          MONTHLY_REPORT_PALETTE.accent500,
          MONTHLY_REPORT_PALETTE.purple500,
          MONTHLY_REPORT_PALETTE.primary400,
          MONTHLY_REPORT_PALETTE.info300,
          MONTHLY_REPORT_PALETTE.accent400
        ],
        borderWidth: 0
      }
    ]
  };

  const sortedCommunities = monthlyReport.topCommunities
    .slice()
    .sort((a, b) => b.all - a.all);

  const communityPerformanceData = {
    labels: sortedCommunities.map(item =>
      item.community.length > 24
        ? `${item.community.substring(0, 24)}…`
        : item.community
    ),
    datasets: [
      {
        label: "Total Tours",
        data: sortedCommunities.map(item => item.all),
        backgroundColor: MONTHLY_REPORT_PALETTE.gray300,
        borderWidth: 0,
        barPercentage: 0.82,
        categoryPercentage: 0.72
      },
      {
        label: "Completed Tours",
        data: sortedCommunities.map(item => item.completed),
        backgroundColor: MONTHLY_REPORT_PALETTE.primary500,
        borderWidth: 0,
        barPercentage: 0.5,
        categoryPercentage: 0.72
      }
    ]
  };

  const heatmapMaxValue = monthlyReport.topAllToursHeatMap.reduce(
    (max, item) => (item.value > max ? item.value : max),
    0
  );

  return (
    <div className={isPrint ? classes.printPage : classes.reportPage}>
      <div className={isPrint ? classes.printPageInner : classes.pageInner}>
        <section className={classes.exportPageSection}>
          <header
            className={`${classes.reportHeader} ${classes.printAvoidBreak} ${
              isPrint ? classes.reportHeaderPrint : ""
            }`}
          >
            <div className={classes.reportHeaderLeft}>
              <div className={classes.brand}>NterNow</div>
              <div
                className={`${classes.reportTitle} ${
                  isPrint ? classes.reportTitlePrint : ""
                }`}
              >
                {monthlyReport.meta.title || "Monthly Operations Brief"}
              </div>
              <div className={classes.reportSubtitle}>
                {monthlyReport.meta.client}
                {" · "}
                High-level operational summary for the selected reporting
                period.
              </div>
            </div>

            <div
              className={`${classes.reportHeaderRight} ${
                isPrint ? classes.reportHeaderRightPrint : ""
              }`}
            >
              <img
                src={NterNowLogo}
                alt="NterNow"
                className={`${classes.logoImage} ${
                  isPrint ? classes.logoImagePrint : ""
                }`}
              />
              <div className={classes.meta}>
                Time Zone: {monthlyReport.meta.timezone}
              </div>
              <div className={classes.dateBadgeWrapper}>
                <span className={classes.dateBadge}>
                  {formatPeriod(startDate, endDate)}
                </span>
              </div>
            </div>
          </header>

          <div className={classes.section}>
            <div className={classes.sectionTag}>Overview</div>
            <div className={classes.sectionTitle}>Monthly Snapshot</div>
          </div>

          <div
            className={`${classes.kpiRow} ${
              isPrint ? classes.kpiRowPrint : ""
            } ${classes.printAvoidBreak}`}
          >
            <div className={classes.kpiItem}>
              <div className={`${classes.kpiCard} ${classes.kpiCardPrimary}`}>
                <div className={`${classes.kpiVal} ${classes.kpiValPrimary}`}>
                  {monthlyReport.summary.totalTours}
                </div>
                <div className={classes.kpiLabel}>Total Tours</div>
              </div>
            </div>

            <div className={classes.kpiItem}>
              <div className={`${classes.kpiCard} ${classes.kpiCardSuccess}`}>
                <div className={`${classes.kpiVal} ${classes.kpiValSuccess}`}>
                  {monthlyReport.summary.completed}
                </div>
                <div className={classes.kpiLabel}>Completed</div>
              </div>
            </div>

            <div className={classes.kpiItem}>
              <div className={`${classes.kpiCard} ${classes.kpiCardDanger}`}>
                <div className={`${classes.kpiVal} ${classes.kpiValDanger}`}>
                  {getMissedCancelledTotal(monthlyReport)}
                </div>
                <div className={classes.kpiLabel}>Missed/Cancelled</div>
              </div>
            </div>

            <div className={classes.kpiItem}>
              <div className={`${classes.kpiCard} ${classes.kpiCardInfo}`}>
                <div className={`${classes.kpiVal} ${classes.kpiValInfo}`}>
                  {formatKpiPercentage(
                    monthlyReport.summary.outsideOfficeHoursPercentage
                  )}
                </div>
                <div className={classes.kpiLabel}>Outside Office Hours</div>
              </div>
            </div>

            <div className={classes.kpiItem}>
              <div className={`${classes.kpiCard} ${classes.kpiCardWarning}`}>
                <div className={`${classes.kpiVal} ${classes.kpiValWarning}`}>
                  {monthlyReport.toursPerVisitor.average}
                </div>
                <div className={classes.kpiLabel}>Avg Tours / Visitor</div>
              </div>
            </div>
          </div>

          <div className={`${classes.highlights} ${classes.printAvoidBreak}`}>
            <div className={classes.highlightsTitle}>Operations Highlights</div>
            <div className={classes.highlightsList}>
              <div className={classes.highlightsItem}>
                <span className={classes.highlightsArrow}>→</span>
                <span>
                  Total tours, completed tours, and missed/cancelled tours are
                  shown up front for faster review.
                </span>
              </div>
              <div className={classes.highlightsItem}>
                <span className={classes.highlightsArrow}>→</span>
                <span>
                  Created scheduled tours older than one hour are classified as
                  missed in the source data.
                </span>
              </div>
              <div className={classes.highlightsItem}>
                <span className={classes.highlightsArrow}>→</span>
                <span>
                  Traffic source is grouped into On Site and Scheduling Site,
                  and Live Support.
                </span>
              </div>
            </div>
          </div>
        </section>

        <section
          className={`${classes.exportPageSection} ${classes.exportPageBreakBefore}`}
        >
          <div className={classes.section}>
            <div className={classes.sectionTag}>Overview</div>
            <div className={classes.sectionTitle}>
              Traffic, Visitor, and Home Mix
            </div>
            <div className={classes.sectionDesc}>
              Core operational charts for the selected reporting period.
            </div>
          </div>

          <Grid container spacing={2} className={classes.grid2}>
            <Grid item xs={12} md={6}>
              <div className={`${classes.card} ${classes.printAvoidBreak}`}>
                <div className={classes.cardTitle}>
                  <span className={`${classes.dot} ${classes.dotPrimary}`} />
                  <span>Tour Status Overview</span>
                </div>
                <div className={`${classes.chartBox} ${classes.chartBoxTall}`}>
                  <HorizontalBar
                    data={statusOverviewData}
                    options={buildHorizontalBarOptions(
                      false,
                      Boolean(isPrint),
                      true
                    )}
                  />
                </div>
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <div className={`${classes.card} ${classes.printAvoidBreak}`}>
                <div className={classes.cardTitle}>
                  <span className={`${classes.dot} ${classes.dotInfo}`} />
                  <span>Traffic Source</span>
                </div>
                <div className={`${classes.chartBox} ${classes.chartBoxDonut}`}>
                  <Doughnut
                    data={sourceChartData}
                    options={buildDoughnutOptions(Boolean(isPrint))}
                  />
                </div>
              </div>
            </Grid>
          </Grid>

          <Grid container spacing={2} className={classes.grid2}>
            <Grid item xs={12} md={6}>
              <div className={`${classes.card} ${classes.printAvoidBreak}`}>
                <div className={classes.cardTitle}>
                  <span className={`${classes.dot} ${classes.dotPurple}`} />
                  <span>Visitor Type</span>
                </div>
                <div className={`${classes.chartBox} ${classes.chartBoxDonut}`}>
                  <Doughnut
                    data={visitorChartData}
                    options={buildDoughnutOptions(Boolean(isPrint))}
                  />
                </div>
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <div className={`${classes.card} ${classes.printAvoidBreak}`}>
                <div className={classes.cardTitle}>
                  <span className={`${classes.dot} ${classes.dotAccent}`} />
                  <span>Home Type</span>
                </div>
                <div className={`${classes.chartBox} ${classes.chartBoxDonut}`}>
                  <Doughnut
                    data={homeTypeChartData}
                    options={buildDoughnutOptions(Boolean(isPrint))}
                  />
                </div>
              </div>
            </Grid>
          </Grid>
        </section>

        <section
          className={`${classes.exportPageSection} ${classes.exportPageBreakBefore}`}
        >
          <div className={classes.section}>
            <div className={classes.sectionTag}>Insights</div>
            <div className={classes.sectionTitle}>Behavior Patterns</div>
            <div className={classes.sectionDesc}>
              Timing and frequency trends shown in a compact operational format.
            </div>
          </div>

          <Grid container spacing={2} className={classes.grid2}>
            <Grid item xs={12} md={6}>
              <div className={`${classes.card} ${classes.printAvoidBreak}`}>
                <div className={classes.cardTitle}>
                  <span className={`${classes.dot} ${classes.dotDark}`} />
                  <span>Tours Per Visitor</span>
                </div>
                <div className={classes.chartBox}>
                  <Bar
                    data={toursPerVisitorData}
                    options={buildBarOptions(Boolean(isPrint))}
                  />
                </div>
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <div className={`${classes.card} ${classes.printAvoidBreak}`}>
                <div className={classes.cardTitle}>
                  <span className={`${classes.dot} ${classes.dotTeal}`} />
                  <span>Tours by Time of Day</span>
                </div>
                <div className={classes.chartBox}>
                  <Bar
                    data={toursByTimeData}
                    options={buildBarOptions(Boolean(isPrint))}
                  />
                </div>
              </div>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <div className={`${classes.card} ${classes.printAvoidBreak}`}>
                <div className={classes.cardTitle}>
                  <span className={`${classes.dot} ${classes.dotInfoDark}`} />
                  <span>Tours by Day</span>
                </div>
                <div className={classes.chartBox}>
                  <Bar
                    data={toursByDayData}
                    options={buildBarOptions(Boolean(isPrint))}
                  />
                </div>
              </div>
            </Grid>
          </Grid>
        </section>

        <section
          className={`${classes.exportPageSection} ${classes.exportPageBreakBefore}`}
        >
          <div className={classes.section}>
            <div className={classes.sectionTag}>Communities</div>
            <div className={classes.sectionTitle}>Community Performance</div>
            <div className={classes.sectionDesc}>
              Top activity, completed-tour rankings, and community detail for
              the selected reporting period.
            </div>
          </div>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <div className={`${classes.card} ${classes.printAvoidBreak}`}>
                <div className={classes.cardTitle}>
                  <span className={`${classes.dot} ${classes.dotDark}`} />
                  <span>Community Performance</span>
                </div>
                <div className={`${classes.chartBox} ${classes.chartBoxTall}`}>
                  <HorizontalBar
                    data={communityPerformanceData}
                    options={buildHorizontalBarOptions(true, Boolean(isPrint))}
                  />
                </div>
              </div>
            </Grid>

            <Grid item xs={12}>
              <div
                className={`${classes.tableCard} ${classes.printAvoidBreak}`}
              >
                <div className={classes.cardTitle}>
                  <span>Community Detail</span>
                </div>
                <div className={classes.tableWrapper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.tableHeadCell}>
                          Community
                        </TableCell>
                        <TableCell
                          className={`${classes.tableHeadCell} ${classes.tableHeadCellCentered}`}
                        >
                          All
                        </TableCell>
                        <TableCell
                          className={`${classes.tableHeadCell} ${classes.tableHeadCellCentered}`}
                        >
                          Completed
                        </TableCell>
                        <TableCell
                          className={`${classes.tableHeadCell} ${classes.tableHeadCellCentered}`}
                        >
                          Missed
                        </TableCell>
                        <TableCell
                          className={`${classes.tableHeadCell} ${classes.tableHeadCellCentered}`}
                        >
                          Cancelled
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {monthlyReport.topCommunities.map(item => (
                        <TableRow key={item.community}>
                          <TableCell
                            className={`${classes.tableBodyCell} ${classes.communityName}`}
                          >
                            {item.community}
                          </TableCell>
                          <TableCell
                            className={`${classes.tableBodyCell} ${classes.tableBodyCellCentered}`}
                          >
                            {item.all}
                          </TableCell>
                          <TableCell
                            className={`${classes.tableBodyCell} ${classes.tableBodyCellCentered}`}
                          >
                            {item.completed > 0 ? (
                              <span
                                className={`${classes.pill} ${classes.pillGreen}`}
                              >
                                {item.completed}
                              </span>
                            ) : (
                              <span className={classes.pillZero}>0</span>
                            )}
                          </TableCell>
                          <TableCell
                            className={`${classes.tableBodyCell} ${classes.tableBodyCellCentered}`}
                          >
                            {item.missed > 0 ? (
                              <span
                                className={`${classes.pill} ${classes.pillRed}`}
                              >
                                {item.missed}
                              </span>
                            ) : (
                              <span className={classes.pillZero}>0</span>
                            )}
                          </TableCell>
                          <TableCell
                            className={`${classes.tableBodyCell} ${classes.tableBodyCellCentered}`}
                          >
                            <span className={classes.pillZero}>
                              {item.cancelled}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </Grid>
          </Grid>
        </section>

        <section
          className={`${classes.exportPageSection} ${classes.exportPageBreakBefore}`}
        >
          <div className={classes.section}>
            <div className={classes.sectionTag}>Heatmaps</div>
            <div className={classes.sectionTitle}>
              Time-of-Day Concentration
            </div>
            <div className={classes.sectionDesc}>
              All-tour heatmap for the selected reporting period.
            </div>
          </div>

          <div className={classes.heatmapPageContent}>
            <div
              className={`${classes.heatmapWrapper} ${classes.printAvoidBreak}`}
            >
              <div className={classes.cardTitle}>
                <span className={`${classes.dot} ${classes.dotPrimary}`} />
                <span>All Tours Time-of-Day Heatmap</span>
              </div>

              <div className={classes.heatmapScroll}>
                <div className={classes.heatmapGrid}>
                  <div />
                  {HEATMAP_DAYS.map(day => (
                    <div key={day} className={classes.heatmapHeader}>
                      {day}
                    </div>
                  ))}

                  {HEATMAP_TIMES.map(time => (
                    <React.Fragment key={time}>
                      <div className={classes.heatmapTime}>{time}</div>
                      {HEATMAP_DAYS.map(day => {
                        const match = monthlyReport.topAllToursHeatMap.find(
                          item => item.day === day && item.hourLabel === time
                        );
                        const value = match ? match.value : 0;

                        return (
                          <div
                            key={`${time}-${day}`}
                            className={`${
                              classes.heatmapCell
                            } ${getHeatmapClassName(
                              value,
                              heatmapMaxValue,
                              classes
                            )}`}
                            title={`${day} ${time}: ${value}`}
                          />
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <div className={classes.legendRow}>
                <span className={classes.legendText}>Less</span>
                <span
                  className={`${classes.legendBox} ${classes.legendBox0}`}
                />
                <span
                  className={`${classes.legendBox} ${classes.legendBox1}`}
                />
                <span
                  className={`${classes.legendBox} ${classes.legendBox2}`}
                />
                <span
                  className={`${classes.legendBox} ${classes.legendBox3}`}
                />
                <span
                  className={`${classes.legendBox} ${classes.legendBox4}`}
                />
                <span className={classes.legendText}>More</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ReportMonthlyContent;
